/**
 * Dynamic Counter - Auto-incrementing social proof counter
 * Initializes with random value and increments by 5% every 3-7 seconds
 * After reaching maximum threshold, fluctuates to maintain realism
 */

class DynamicCounter {
  constructor(element) {
    this.element = element;
    this.counterId = element.dataset.counterId;
    this.counterElement = element.querySelector('.dynamic-counter-value');

    if (!this.counterElement) return;

    // Get configuration from data attributes
    this.config = {
      enableTimeBased: element.dataset.enableTimeBased === 'true',
      minValue: parseInt(element.dataset.minValue) || 20,
      maxValue: parseInt(element.dataset.maxValue) || 100,
      nightMin: parseInt(element.dataset.nightMin) || 10,
      nightMax: parseInt(element.dataset.nightMax) || 40,
      morningMin: parseInt(element.dataset.morningMin) || 30,
      morningMax: parseInt(element.dataset.morningMax) || 80,
      dayMin: parseInt(element.dataset.dayMin) || 50,
      dayMax: parseInt(element.dataset.dayMax) || 150,
      eveningMin: parseInt(element.dataset.eveningMin) || 40,
      eveningMax: parseInt(element.dataset.eveningMax) || 120,
      incrementMin: parseInt(element.dataset.incrementMin) || 3,
      incrementMax: parseInt(element.dataset.incrementMax) || 7,
      maxMultiplier: parseInt(element.dataset.maxMultiplier) || 10,
      enablePlateau: element.dataset.enablePlateau === 'true'
    };

    this.init();
  }

  /**
   * Get current hour in US Eastern timezone
   */
  getEasternHour() {
    const now = new Date();
    const easternTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      hour12: false
    }).format(now);
    return parseInt(easternTime);
  }

  /**
   * Get min/max values based on current time
   */
  getTimeBasedRange() {
    if (!this.config.enableTimeBased) {
      return {
        min: this.config.minValue,
        max: this.config.maxValue
      };
    }

    const hour = this.getEasternHour();

    if (hour >= 0 && hour < 6) {
      // Night: 00:00-06:00
      return { min: this.config.nightMin, max: this.config.nightMax };
    } else if (hour >= 6 && hour < 12) {
      // Morning: 06:00-12:00
      return { min: this.config.morningMin, max: this.config.morningMax };
    } else if (hour >= 12 && hour < 18) {
      // Day: 12:00-18:00
      return { min: this.config.dayMin, max: this.config.dayMax };
    } else {
      // Evening: 18:00-24:00
      return { min: this.config.eveningMin, max: this.config.eveningMax };
    }
  }

  /**
   * Get random number between min and max (inclusive)
   */
  getRandomNumber(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  /**
   * Get random interval in milliseconds
   */
  getRandomInterval() {
    return this.getRandomNumber(
      this.config.incrementMin * 1000,
      this.config.incrementMax * 1000
    );
  }

  /**
   * Initialize counter value
   */
  init() {
    const sessionKey = `counter_${this.counterId}`;
    const initialValueKey = `counter_initial_${this.counterId}`;
    const storedValue = sessionStorage.getItem(sessionKey);
    const storedInitialValue = sessionStorage.getItem(initialValueKey);

    if (storedValue && storedInitialValue) {
      // Use stored values from session
      this.currentValue = parseInt(storedValue);
      this.initialValue = parseInt(storedInitialValue);
    } else {
      // Initialize with random value from range
      const range = this.getTimeBasedRange();
      this.initialValue = this.getRandomNumber(range.min, range.max);
      this.currentValue = this.initialValue;
      sessionStorage.setItem(sessionKey, this.currentValue);
      sessionStorage.setItem(initialValueKey, this.initialValue);
    }

    // Calculate maximum threshold
    this.maxThreshold = Math.ceil(this.initialValue * this.config.maxMultiplier);

    this.updateDisplay();
    this.startIncrementing();
  }

  /**
   * Update counter display
   */
  updateDisplay() {
    this.counterElement.textContent = this.currentValue;
  }

  /**
   * Check if counter has reached plateau
   */
  hasReachedPlateau() {
    return this.config.enablePlateau && this.currentValue >= this.maxThreshold;
  }

  /**
   * Increment or fluctuate counter based on current state
   */
  updateCounter() {
    const sessionKey = `counter_${this.counterId}`;

    if (this.hasReachedPlateau()) {
      // Plateau behavior: fluctuate +/- 5% around max threshold
      const fluctuation = Math.ceil(this.maxThreshold * 0.05);
      const direction = Math.random() > 0.5 ? 1 : -1;
      const change = this.getRandomNumber(1, fluctuation) * direction;

      this.currentValue = this.currentValue + change;

      // Keep within reasonable bounds (90% to 110% of max threshold)
      const minBound = Math.ceil(this.maxThreshold * 0.9);
      const maxBound = Math.ceil(this.maxThreshold * 1.1);
      this.currentValue = Math.max(minBound, Math.min(maxBound, this.currentValue));
    } else {
      // Normal growth: increment by 5%
      const increment = Math.ceil(this.currentValue * 0.05);
      this.currentValue += increment;

      // Cap at maximum threshold
      if (this.currentValue > this.maxThreshold) {
        this.currentValue = this.maxThreshold;
      }
    }

    // Store in session
    sessionStorage.setItem(sessionKey, this.currentValue);

    this.updateDisplay();
  }

  /**
   * Start auto-updating at random intervals
   */
  startIncrementing() {
    const scheduleNext = () => {
      const interval = this.getRandomInterval();
      setTimeout(() => {
        this.updateCounter();
        scheduleNext();
      }, interval);
    };

    scheduleNext();
  }
}

// Initialize all dynamic counters on page load
document.addEventListener('DOMContentLoaded', () => {
  const counters = document.querySelectorAll('.dynamic-counter-block');
  counters.forEach(counter => new DynamicCounter(counter));
});

// Re-initialize on Shopify section reload (theme editor)
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    const counters = document.querySelectorAll('.dynamic-counter-block');
    counters.forEach(counter => new DynamicCounter(counter));
  });
}
